import { FC } from 'react'
import {
  Card,
  Typography,
  Row,
  Col,
  Slider,
  Badge,
  MultiplyBadge,
  SectionSeparator,
} from '@repo/ui/components'
import { MessagesKeys, useTranslate } from '@repo/shared/hooks'
import { cn } from '@repo/ui/lib'
import Image from 'next/image'
import { ButtonLink, ValueWithUnit } from '@repo/shared/components'
import { useGetLoyaltyPortfolio } from '@repo/services'

export interface MissionCardProps {
  name: string
  title: string
  subTitle: string
  hintPoints: number[]
  points: number
  progress?: { current: number; total: number }
  state: 'active' | 'disable' | 'completed'
  actions: { type: 'link'; name: string; url: string }[]
  receivedPoints: number
  unmetDependencies: string[]
}

export const MissionCard: FC<MissionCardProps> = ({
  name,
  title,
  subTitle,
  hintPoints,
  points,
  progress,
  state,
  actions,
  unmetDependencies,
}) => {
  const { t } = useTranslate()

  const { data: { data: { portfolio = {} } = {} } = {} } =
    useGetLoyaltyPortfolio()
  const coefficient = Math.floor((portfolio.coefficient ?? 0) * 100) / 100
  return (
    <>
      <Card withoutBorder className='gap-md p-lg flex flex-col h-full w-full'>
        <div className='flex flex-col md:flex-row justify-between items-center gap-md'>
          <Col
            className={cn(
              'w-full gap-xs',
              state === 'disable' ? 'opacity-30' : '',
            )}
          >
            <Typography
              size='md'
              weight='bold'
              color='text-neutral-surface-100'
            >
              {title}
            </Typography>
            <Typography
              size='sm'
              color='text-neutral-surface-80'
              className='flex flex-col gap-xs'
            >
              {subTitle}
            </Typography>
            {name && hintPoints.length > 0 && (
              <>
                {hintPoints.map((value, index) => (
                  <Row
                    key={`${name}-${index}`}
                    className='justify-between items-start'
                  >
                    <Typography
                      size='sm'
                      color='text-neutral-surface-80'
                      key={index}
                    >
                      {t(
                        `clubPage_mission_item_${name}_description_points_${index + 1}` as MessagesKeys,
                      )}
                    </Typography>
                    <Row className='gap-2xs items-center w-fit'>
                      <ValueWithUnit
                        value={value}
                        // unit='square_token'
                        valueSize='sm'
                        unitSize='2xs'
                        valueWeight='regular'
                      />
                      <Image
                        src='/images/club-gem.svg'
                        alt='coin'
                        width={20}
                        height={20}
                        className='-mt-1'
                      />
                    </Row>
                  </Row>
                ))}
              </>
            )}
            {progress && (
              <Slider
                value={Array.from(
                  { length: progress.current + 1 },
                  (_, i) => i,
                )}
                max={progress.total}
                color='positiveLight'
                className='mt-sm'
                showInactiveThumbs={true}
              />
            )}
          </Col>
          <div
            className={cn(
              'w-full md:w-fit min-w-[176px] gap-md flex flex-row md:flex-col justify-between items-center',
              state !== 'active' ? 'flex-row-reverse' : 'flex-row',
            )}
          >
            {state === 'disable' && (
              <Typography
                size='xs'
                className='text-center max-w-[118px] 3xs:max-w-[156px] md:max-w-full'
                color='text-neutral-surface-80'
              >
                {t('clubPage_mission_blocked_description', {
                  title: unmetDependencies.join(', '),
                })}
              </Typography>
            )}
            {state === 'completed' && (
              <Badge variant='liquid' color='positive' className='w-fit'>
                {t('clubPage_mission_state_done')}
              </Badge>
            )}

            {state !== 'completed' && (
              <Row
                className={cn(
                  state === 'disable' ? 'grayscale' : '',
                  'w-fit  justify-between gap-xs md:gap-2xs',
                )}
              >
                <Row>
                  <Image
                    src='/images/club-gem.svg'
                    alt='coin'
                    width={20}
                    height={20}
                  />
                  <ValueWithUnit
                    value={points}
                    valueSize='sm'
                    unitSize='2xs'
                    valueWeight='bold'
                  />
                </Row>

                <MultiplyBadge value={coefficient} size='xs' />
              </Row>
            )}
            {actions.length > 0 && state === 'active' && (
              <ButtonLink
                variant='ghost'
                size='sm'
                className='w-full flex-1'
                width='full'
                buttonClassName='w-full flex-1'
                href={actions[0]?.url ?? ''}
              >
                {actions[0]?.name}
              </ButtonLink>
            )}
            {state === 'completed' && (
              <Row className='gap-3xs items-center w-fit'>
                <ValueWithUnit
                  value={points}
                  unit={'square_token'}
                  valueSize='xs'
                  unitSize='2xs'
                  valueWeight='bold'
                />
                <Typography size='2xs' color='text-neutral-surface-80'>
                  {t('clubPage_mission_done_description')}
                </Typography>
              </Row>
            )}
          </div>
        </div>
      </Card>
      <SectionSeparator />
    </>
  )
}
